<template lang="pug"></template>

<script>
export default {
    name: 'FloatBox',

    props: {
        botoes: Array
    },

    data() {
        return {
            expanded: false,
            buttons: [],
            viewComponents: [],
            currentComponent: null,
            contentTitle: '',
            contentTitleIconClass: null
        };
    },
    watch: {
        botoes: {
            immediate: true,
            handler: function (someData) {
                // check someData and eventually call
                if (!this.botoes) return;

                this.buttons = [];

                for (let componentClass of this.botoes) {
                    this.configureComponent(componentClass);
                }
            }
        }
    },

    methods: {
        toogle() {
            this.expanded = !this.expanded;
        },

        collapse() {
            this.expanded = false;
        },

        changeComponent(componentClass, callback) {
            var viewChanged = this.currentComponent !== componentClass.name;
            var lastComponent = this.getComponentByName(this.currentComponent);

            // Se este componente já estiver aberto, contrair o menu
            if (this.expanded && !viewChanged) {
                this.collapse();
                return;
            }

            // Se alterou o componente, chamar o método "beforeComponentHide" do
            // componente anterior, antes de substituí-lo pelo novo.
            if (
                viewChanged &&
                lastComponent &&
                lastComponent.beforeComponentHide
            ) {
                lastComponent.beforeComponentHide();
            }

            this.expanded = true;
            this.currentComponent = componentClass.name;
            this.contentTitle = componentClass.componentOptions.title;
            this.contentTitleIconClass =
                componentClass.componentOptions.titleIconClass;

            var newComponent = this.getComponentByName(this.currentComponent);

            if (newComponent) {
                if (viewChanged && newComponent.afterComponentShow) {
                    newComponent.afterComponentShow();
                }

                if (callback) callback(newComponent);
            } else {
                this.$nextTick(() => {
                    newComponent = this.getComponentByName(
                        this.currentComponent
                    );

                    if (newComponent) {
                        if (newComponent.afterComponentShow) {
                            newComponent.afterComponentShow();
                        }

                        if (callback) callback(newComponent);
                    }
                });
            }
        },

        // Executa a ação de um dos botões. Se a action informada no parâmetro
        // podendo expandir a barra de conteúdo (action.showView) e/ou executar
        // uma função do componente (action.action)
        callAction(action) {
            if (action.showView) {
                this.changeComponent(action.componentClass);
            }

            if (action.action) {
                this.currentComponent = action.componentClass.name;

                this.$nextTick(() => {
                    var component = this.getComponentByName(
                        this.currentComponent
                    );
                    component[action.action]();
                });
            }
        },

        getComponentByName(name) {
            if (!name || !this.$refs[name]) return null;

            return this.$refs[name][0];
        },

        getCurrentComponent() {
            return (
                this.currentComponent &&
                this.$refs[this.currentComponent] &&
                this.$refs[this.currentComponent][0]
            );
        },

        // Adiciona um componente à barra de ferramentas. ComponentClass deve
        // ser uma definição de componente Vue e deve possuir o atributo:
        // "componentOptions: { title: 'Título', actions: [] }".
        // Actions é um array contendo arrays (grupos) de objetos (actions).
        // Cada action é um botão a ser adicionado na barra e possui a estrutura:
        // { name: 'Camadas', iconClass: 'fa fa-bars', action: () => this.show(); showView: true }
        configureComponent(componentClass) {
            var options = componentClass.componentOptions;
            var errorMsg =
                'Erro ao carregar o componente ' + componentClass.name + '. ';

            if (!options) {
                errorMsg +=
                    'O componente não possui a propriedade "componentOptions".';
                throw errorMsg;
            }

            this.viewComponents.push(componentClass);

            // Configurando as action do componente, que são as ações acionadas através
            // dos botões da barra de ferramentas.
            if (options.actions) {
                for (let actionGroup of options.actions) {
                    for (let action of actionGroup) {
                        action.componentClass = componentClass;
                        options.titleIconClass =
                            options.titleIconClass || action.iconClass;

                        if (action.showView && !options.title) {
                            errorMsg +=
                                'O componente não possui a propriedade "componentOptions.title".';
                            throw errorMsg;
                        }
                    }
                }
            }

            this.buttons = this.buttons.concat(options.actions);

            // Configurando os eventos que devem ser tratados pelo componente. Estes eventos devem ser
            // disparados através do barramento ($bus).
            if (options.events) {
                for (let event in options.events) {
                    this.$bus.$on(event, e =>
                        this.handleComponentEvent(
                            e,
                            componentClass,
                            options.events[event]
                        )
                    );
                }
            }
        },

        handleComponentEvent(event, componentClass, handlerName) {
            if (this.currentComponent === componentClass.name) {
                this.getCurrentComponent()[handlerName](event);
            } else {
                this.changeComponent(componentClass, component => {
                    component[handlerName](event);
                });
            }
        },

        closeCurrentComponent() {
            var component = this.getCurrentComponent();

            if (component && component.beforeComponentHide) {
                component.beforeComponentHide();
            }

            this.currentComponent = null;
            this.contentTitle = null;
            this.contentTitleIconClass = null;

            this.collapse();
        }
    }
};
</script>

<style lang="scss">
$float-box-title-padding-left: 15px;

.float-box {
    z-index: 1099;
    position: absolute;
    top: 0;
    bottom: 0;

    transform: translateX(0);
    -webkit-transform: translateX(0);
    animation-fill-mode: forwards;
    transition-property: -webkit-transform, transform, opacity;
    transition-duration: 0.3s;
    transition-timing-function: cubic-bezier(0, 0, 0.2, 1);

    .float-box-view {
        background-color: var(--white);
        width: 100%;
        height: 100%;
        background-color: var(--white);
        display: block;
        position: relative;
        overflow-x: hidden;
    }

    .float-box-collapse-icon {
        position: absolute;
        right: 15px;
        top: $float-box-title-padding-top - 2px;
        z-index: 10001;
        font-size: $float-box-close-icon-size;
    }

    .float-box-title {
        width: 100%;
        background-color: var(--float-box-bg-color);
        height: $float-box-title-height;

        h2 {
            padding-top: $float-box-title-padding-top;
            font-size: $float-box-title-font-size;
            padding-right: $float-box-close-icon-size;
            padding-left: $float-box-title-padding-left + 7px;
            width: 100%;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;

            i {
                position: absolute;
                left: 0;
                padding-left: 15px;
                top: 0;
                bottom: 0;
                width: $float-box-title-padding-left +
                    $float-box-title-font-size + 7px;
                line-height: $float-box-title-height;
                vertical-align: middle;
            }
        }
    }

    .float-box-content .nav.nav-icons,
    .float-box-title-extras .nav.nav-icons {
        padding-top: 10px;
        padding-bottom: 10px;

        li.nav-item a.nav-link {
            color: var(--float-box-nav-icons-color);
            padding: 0;

            i,
            small {
                width: 100%;
                display: block;
                text-align: center;
            }

            i {
                height: 28px;
                font-size: 20px;
            }

            small {
                font-size: 13px;
            }

            &.active {
                color: var(--primary);
            }
        }

        li.nav-item + li.nav-item {
            border-left: solid 1px var(--float-box-title-icons-hr-color);
        }
    }

    .float-box-title-extras .nav.nav-icons {
        padding-top: 0;
    }

    .float-box-title-extras {
        padding-top: $float-box-title-extras-padding;
        padding-bottom: $float-box-title-extras-padding;
        background-color: var(--float-box-bg-color);

        hr {
            margin-top: -$float-box-title-extras-padding;
            border-top: 1px solid var(--float-box-title-icons-hr-color);
        }
    }

    .float-box-nav {
        background-color: var(--float-box-bg-color);
        border: none;

        .nav-link {
            color: var(--float-box-nav-font-color);
            padding-top: 0px;
            padding-bottom: 6px;
            border: 0;
            border-bottom: solid 2px var(--float-box-title-icons-hr-color);
            font-size: $float-box-nav-font-size;

            &.active {
                background-color: var(--float-box-bg-color);
                border-bottom: solid 2px var(--primary);
            }

            &:hover,
            &.active {
                color: var(--float-box-nav-active-font-color);
            }
        }
    }

    .float-box-content {
        top: $float-box-content-top;
        bottom: $float-box-content-padding-bottom;
        right: 0;
        width: 100%;
        position: absolute;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: $float-box-content-padding-bottom;
        padding-top: 0;

        &.default-padding,
        .float-box-default-padding {
            padding-top: $float-box-content-padding-top;
        }
    }

    .float-box-content-menu {
        box-sizing: border-box;
        font-size: $float-box-content-menu-font-size;
        color: var(--primary);
        line-height: $float-box-content-menu-height;
        vertical-align: middle;

        .action-link {
            height: $float-box-content-menu-height;
            cursor: pointer;
            padding-left: $float-box-content-menu-action-link-padding;
            padding-right: $float-box-content-menu-action-link-padding;

            &:hover {
                color: var(--primary);
            }
        }

        &.float-box-content-menu-border {
            border-bottom: solid 1px var(--float-box-title-icons-hr-color);
        }
    }

    .float-box-view-level-0,
    .float-box-view-level-1,
    .float-box-view-level-2 {
        width: 100%;
        position: absolute;
        background-color: var(--white);
        box-sizing: border-box;
        top: 0;
        bottom: 0;

        animation-fill-mode: forwards;
        transition-property: -webkit-transform, transform, opacity;
        transition-duration: 0.3s;
        transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
    }

    .float-box-view-level-0 {
        transform: translateX(0);
        -webkit-transform: translateX(0);
    }

    .float-box-view-level-1 {
        transform: translateX(100%);
        -webkit-transform: translateX(100%);
    }

    .float-box-view-level-2 {
        transform: translateX(200%);
        -webkit-transform: translateX(200%);
    }

    .float-box-view.level-1,
    .float-box-view .level-1 {
        .float-box-view-level-0 {
            transform: translateX(-100%);
            -webkit-transform: translateX(-100%);
            .float-box-content {
                overflow-y: hidden;
            }
        }
        .float-box-view-level-1 {
            transform: translateX(0);
            -webkit-transform: translateX(0);
        }
    }

    .float-box-view.level-2,
    .float-box-view .level-2 {
        .float-box-view-level-0,
        .float-box-view-level-1 {
            transform: translateX(-100%);
            -webkit-transform: translateX(-100%);
            .float-box-content {
                overflow-y: hidden;
            }
        }

        .float-box-view-level-2 {
            transform: translateX(0);
            -webkit-transform: translateX(0);
        }
    }

    .float-box-section-title {
        color: var(--float-box-section-title-color);
        font-size: 14px;
        width: 100%;

        label {
            display: inline;
            background-color: var(--white);
            padding-right: 5px;
        }

        hr {
            border-color: var(--float-box-section-hr-color);
            margin-top: -11px;
            margin-bottom: 0;
        }

        margin-top: 8px;
        margin-bottom: 15px;
    }

    * + .float-box-section-title {
        margin-top: 20px;
    }

    .float-box-footer {
        background-color: var(--float-box-bg-color);
        position: absolute;
        bottom: 0;
        width: 100%;
        overflow-y: hidden;
        overflow-x: hidden;
        height: $float-box-footer-height;
        line-height: $float-box-footer-height;
    }

    .alert {
        @include border-radius(0);
    }
}
</style>
